/**
 * Standalone R3F preview canvas for the Particle Editor.
 * Renders the selected effect composite with bloom postprocessing.
 *
 * For thruster entries, always renders ThrusterExhaust (which includes ThrusterSparks
 * as a child) — this is WYSIWYG: the preview matches in-game appearance.
 *
 * Reads effectId from the zustand store (not props) because R3F Canvas
 * creates a separate React reconciler — props from the DOM tree may not trigger
 * re-renders in the R3F fiber tree. Zustand subscriptions work reliably across
 * the reconciler boundary via useSyncExternalStore.
 *
 * Camera controls: left-drag to pan, mouse wheel to zoom, double-click to reset.
 */

import { Suspense, useEffect, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import type { PerspectiveCamera } from 'three';
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  BloomEffect,
  BlendFunction,
} from 'postprocessing';
import { HalfFloatType, NoToneMapping } from 'three';
import { ThrusterExhaust } from '../effects/ThrusterExhaust.js';
import { useParticleEffectStore } from '../../stores/particleEffectStore.js';
import styles from './ParticleEditor.module.css';

interface PreviewProps {
  readonly playing: boolean;
  readonly afterburner: boolean;
}

function ThrusterPreview({ afterburner }: { afterburner: boolean }) {
  return (
    <group>
      <ThrusterExhaust active afterburner={afterburner} />
    </group>
  );
}

const DEFAULT_CAM_Y = 8;
const ZOOM_MIN = 2;
const ZOOM_MAX = 30;
const ZOOM_SPEED = 0.001;

/** Pan (left-drag) and zoom (mouse wheel) controls for the preview camera. Double-click resets. */
function PreviewCameraControls() {
  const { camera, gl } = useThree();
  const dragRef = useRef({ active: false, lastX: 0, lastY: 0 });

  useEffect(() => {
    const el = gl.domElement;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button === 0) {
        dragRef.current = { active: true, lastX: e.clientX, lastY: e.clientY };
        el.setPointerCapture(e.pointerId);
        el.style.cursor = 'grabbing';
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragRef.current.active) return;
      const dx = e.clientX - dragRef.current.lastX;
      const dy = e.clientY - dragRef.current.lastY;
      dragRef.current.lastX = e.clientX;
      dragRef.current.lastY = e.clientY;

      // Convert pixel delta to world units based on camera height and FOV
      const fovRad = ((camera as PerspectiveCamera).fov * Math.PI) / 180;
      const worldHeight = 2 * camera.position.y * Math.tan(fovRad / 2);
      const scale = worldHeight / el.clientHeight;

      // Drag-to-grab: camera moves opposite to drag direction
      // Camera up = (0,0,-1): screen-right = +X, screen-up = -Z
      camera.position.x -= dx * scale;
      camera.position.z -= dy * scale;
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.button === 0) {
        dragRef.current.active = false;
        el.style.cursor = 'grab';
      }
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = 1 + e.deltaY * ZOOM_SPEED;
      camera.position.y = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, camera.position.y * factor));
    };

    const onDoubleClick = () => {
      camera.position.set(0, DEFAULT_CAM_Y, 0);
    };

    el.style.cursor = 'grab';
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('dblclick', onDoubleClick);

    return () => {
      el.style.cursor = '';
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('dblclick', onDoubleClick);
    };
  }, [camera, gl]);

  return null;
}

function TimeController({ playing }: { playing: boolean }) {
  const clockRef = useRef<boolean>(false);

  useFrame(({ clock }) => {
    if (!playing) {
      if (!clockRef.current) {
        clockRef.current = true;
      }
      clock.stop();
    } else {
      if (clockRef.current) {
        clockRef.current = false;
      }
      if (!clock.running) {
        clock.start();
      }
    }
  });

  return null;
}

/** Minimal bloom postprocessing for the preview canvas. */
function PreviewBloom() {
  const { gl, scene, camera, size } = useThree();
  const composerRef = useRef<EffectComposer | null>(null);

  useEffect(() => {
    gl.toneMapping = NoToneMapping;

    const composer = new EffectComposer(gl, {
      frameBufferType: HalfFloatType,
    });

    const bloom = new BloomEffect({
      blendFunction: BlendFunction.ADD,
      luminanceThreshold: 0.2,
      luminanceSmoothing: 0.3,
      intensity: 1.5,
      mipmapBlur: true,
      radius: 0.4,
    });

    composer.addPass(new RenderPass(scene, camera));
    composer.addPass(new EffectPass(camera, bloom));
    composerRef.current = composer;

    return () => {
      composer.dispose();
      composerRef.current = null;
    };
  }, [gl, scene, camera]);

  useEffect(() => {
    composerRef.current?.setSize(size.width, size.height);
  }, [size]);

  useFrame((_, delta) => {
    if (composerRef.current) {
      composerRef.current.render(delta);
    }
  }, 1);

  return null;
}

function PreviewScene({ playing, afterburner }: PreviewProps) {
  // Read effectId from store via zustand subscription — works across R3F reconciler boundary
  const effectId = useParticleEffectStore((s) => s.selectedPreviewEffect);

  return (
    <>
      <PreviewCameraControls />
      <TimeController playing={playing} />
      <PreviewBloom />
      <ambientLight intensity={0.3} />
      <pointLight position={[0, 10, 0]} intensity={200} />
      {effectId === 'thruster_flame' && <ThrusterPreview afterburner={afterburner} />}
      {effectId === 'atmosphere_wisps' && (
        <mesh>
          <sphereGeometry args={[1, 16, 16]} />
          <meshStandardMaterial color="#334" wireframe />
        </mesh>
      )}
    </>
  );
}

export function ParticleEditorPreview({ playing, afterburner }: PreviewProps) {
  return (
    <div className={styles.previewCanvas}>
      <Suspense fallback={null}>
        <Canvas
          camera={{ position: [0, 8, 0], fov: 50, near: 0.1, far: 200, up: [0, 0, -1] }}
          gl={{ antialias: false, alpha: false, stencil: false, depth: false }}
          style={{ background: '#0a0a0e' }}
        >
          <color attach="background" args={['#0a0a0e']} />
          <PreviewScene playing={playing} afterburner={afterburner} />
        </Canvas>
      </Suspense>
    </div>
  );
}
